// src/store/modules/auth.js

import axios from 'axios';

/// The whole session, as one object.
///
/// The individual keys below are what the rest of the app reads, and
/// they stay. This is only used to park the god user's own session
/// while they are inside somebody else's: seven separate backup keys
/// would be seven chances to forget one, and forgetting one means the
/// user cannot get back out.
const ORIGINAL_KEY = 'impersonation_original';

function snapshot(state) {
  return {
    id:           state.id,
    username:     state.username,
    token:        state.token,
    role:         state.role,
    customerId:   state.customerId,
    customerName: state.customerName,
    email:        state.email,
  };
}

function loadOriginal() {
  try {
    return JSON.parse(localStorage.getItem(ORIGINAL_KEY) || 'null');
  } catch {
    return null;
  }
}

const state = {
  id:           parseInt(localStorage.getItem('userId'))    || 0,
  username:     localStorage.getItem('username')            || '',
  token:        localStorage.getItem('userToken')           || '',
  role:         localStorage.getItem('userRole')            || 'user',
  customerId:   parseInt(localStorage.getItem('customerId')) || null,
  customerName: localStorage.getItem('customerName')        || '',
  email:        localStorage.getItem('userEmail')           || '',

  /// The god user's own session, parked while impersonating. Null
  /// otherwise — and its presence is what "am I impersonating" means.
  ///
  /// Restored from localStorage on load so a page refresh in the middle
  /// of a support session does not strand the user inside the
  /// customer's account with no way back.
  original:     loadOriginal(),
};

const mutations = {
  SET_USER(state, { userId, userName, role, customerId, customerName, email }) {
    state.id           = userId;
    state.username     = userName;
    state.role         = role         || 'user';
    state.customerId   = customerId;
    state.customerName = customerName || '';
    state.email        = email        || '';

    localStorage.setItem('userId',       userId);
    localStorage.setItem('username',     userName);
    localStorage.setItem('userRole',     role || 'user');
    localStorage.setItem('customerId',   customerId);
    localStorage.setItem('customerName', customerName || '');
    localStorage.setItem('userEmail',    email || '');
  },

  SET_TOKEN(state, token) {
    state.token = token;
    localStorage.setItem('userToken', token);
  },

  /// Park the current session and switch to the target's.
  ///
  /// Everything downstream — apiBase/authHeaders, the axios interceptor,
  /// every store module — reads userToken and customerId from
  /// localStorage, so overwriting them is all it takes for the entire
  /// app to be inside the other account. That is the appeal of the
  /// design and also its risk, which is what the banner is for.
  START_IMPERSONATION(state, { token, user }) {
    if (!state.original) {
      state.original = snapshot(state);
      localStorage.setItem(ORIGINAL_KEY, JSON.stringify(state.original));
    }

    state.id           = user.userId;
    state.username     = user.name;
    state.role         = user.role || 'user';
    state.customerId   = user.customerId;
    state.customerName = user.customerName || '';
    state.email        = user.email || '';
    state.token        = token;

    localStorage.setItem('userId',       user.userId);
    localStorage.setItem('username',     user.name);
    localStorage.setItem('userRole',     user.role || 'user');
    localStorage.setItem('customerId',   user.customerId);
    localStorage.setItem('customerName', user.customerName || '');
    localStorage.setItem('userEmail',    user.email || '');
    localStorage.setItem('userToken',    token);
  },

  END_IMPERSONATION(state) {
    const o = state.original;
    if (!o) return;

    state.id           = o.id;
    state.username     = o.username;
    state.token        = o.token;
    state.role         = o.role;
    state.customerId   = o.customerId;
    state.customerName = o.customerName;
    state.email        = o.email;
    state.original     = null;

    localStorage.setItem('userId',       o.id);
    localStorage.setItem('username',     o.username);
    localStorage.setItem('userToken',    o.token);
    localStorage.setItem('userRole',     o.role);
    localStorage.setItem('customerId',   o.customerId);
    localStorage.setItem('customerName', o.customerName);
    localStorage.setItem('userEmail',    o.email);
    localStorage.removeItem(ORIGINAL_KEY);
  },

  CLEAR_USER(state) {
    state.id           = 0;
    state.username     = '';
    state.token        = '';
    state.role         = 'user';
    state.customerId   = null;
    state.customerName = '';
    state.email        = '';
    state.original     = null;

    localStorage.removeItem('userId');
    localStorage.removeItem('username');
    localStorage.removeItem('userToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('customerId');
    localStorage.removeItem('customerName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem(ORIGINAL_KEY);
  },
};

const actions = {
  async login({ commit }, { username, password }) {
    try {
      const baseURL = process.env.VUE_APP_API_URL || 'http://127.0.0.1/api';
      const response = await axios.post(`${baseURL}/auth/login`, { username, password });
      const data = response.data;

      commit('SET_USER', {
        userId:       data.userId,
        userName:     data.username,
        role:         data.role,
        customerId:   data.customerId,
        customerName: data.customerName,
        email:        data.email,
      });
      commit('SET_TOKEN', data.token);

    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  },

  logout({ commit }) {
    commit('CLEAR_USER');
  },

  // ── Impersonation ────────────────────────────────────────────────

  /// Step into a customer's account.
  ///
  /// The server decides WHICH of their users to borrow and returns it —
  /// role included. Do not assume a role here: MobilePrint hardcodes
  /// 'admin' on the impersonated user, which is wrong the moment the
  /// only account in a customer is an ordinary one, and it silently
  /// gives support a menu the customer does not actually have.
  async startImpersonation({ commit }, customerId) {
    try {
      const { default: api } = await import('@/services/api');
      const { data } = await api.post('/admin/impersonate', { customerId });

      commit('START_IMPERSONATION', {
        token: data.token,
        user: {
          userId:       data.impersonating.userId,
          name:         data.impersonating.name,
          role:         data.impersonating.role,
          customerId:   data.impersonating.customerId,
          customerName: data.impersonating.customerName,
          email:        data.impersonating.email,
        },
      });

      return { ok: true, customerName: data.impersonating.customerName };
    } catch (err) {
      return {
        ok: false,
        error: err.response?.data?.error || 'Impersonation failed',
      };
    }
  },

  /// Step back out.
  ///
  /// The local restore happens whatever the server says. The call
  /// stamps the audit row, but if it fails — network gone, session
  /// already expired — leaving the user trapped inside somebody else's
  /// account would be the worse outcome by a wide margin.
  async endImpersonation({ commit }) {
    try {
      const { default: api } = await import('@/services/api');
      await api.post('/admin/impersonate/end');
    } catch (err) {
      console.error('Failed to record impersonation end:', err);
    } finally {
      commit('END_IMPERSONATION');
    }
  },
};

const getters = {
  isLoggedIn:    state => !!state.token,
  isGod:         state => state.role === 'god',
  isSuperAdmin:  state => state.role === 'superadmin' || state.role === 'god',
  isLocationAdmin: state => state.role === 'location_admin' || state.role === 'superadmin' || state.role === 'god',
  isUser:        state => !!state.token,
  role:          state => state.role,
  customerId:    state => state.customerId,
  customerName:  state => state.customerName,
  userId:        state => state.id,

  /// Are we inside somebody else's account right now?
  isImpersonating: state => !!state.original,

  /// The god user behind an impersonated session, for the banner.
  originalUser:    state => state.original,

  /// A REAL god, not a borrowed account that happens to be privileged.
  ///
  /// This is what gates the impersonation entry point. Without the
  /// second clause, stepping into a customer whose admin is somehow a
  /// god would show that session the customer list — and from there
  /// they could hop onwards. The server refuses it too; this stops the
  /// menu item from appearing at all.
  canImpersonate: state => state.role === 'god' && !state.original,

  // Convenience: can this user manage locations?
  canManageLocations: state => ['god', 'superadmin'].includes(state.role),
  // Can this user manage users?
  canManageUsers: state => ['god', 'superadmin'].includes(state.role),
  // Can this user edit products/categories?
  canEditContent: state => ['god', 'superadmin', 'location_admin'].includes(state.role),
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};